import React, { useEffect, useState } from "react";
import { SortableContainer, SortableContainerProps, SortableElement, SortableElementProps } from "react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useTheme } from "@mui/material";
import { Button } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { CustomDialog } from "components/dialogs/Dialogs";
import { WidgetTemplate } from "view/dashboard/types/widget";
import "view/statuses/styling/styling.scss";

type WidgetOrderDialogProps = {
	open: boolean;
	onClose: () => void;
	widgets: Array<WidgetTemplate>;
	onSave: (widgets: Array<WidgetTemplate>) => void;
};

const WidgetOrderDialog = ({ open, onClose, widgets, onSave }: WidgetOrderDialogProps) => {

	const [ list, setList ] = useState<Array<WidgetTemplate>>([]);

	const theme = useTheme();

	const handleOnClose = () => {
		onClose();
		setList([]);
	};

	const save = () => {
		handleOnClose();
		onSave(list);
	};

	const SortableItem: React.ComponentClass<SortableElementProps & { item: WidgetTemplate }> = (
		SortableElement(({ item }: { item: WidgetTemplate }) => (
			<div
				className="sortable-item"
				style={{
					backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#FFF",
					borderBottom: theme.palette.mode === "dark" ? "1px solid rgba(0, 0, 0, 0.87)" : "1px solid #F0F0F0",
					color: theme.palette.mode === "dark" ? "#fff" : "#333",
					gap: 8,
				}}>
				<DragIndicatorIcon fontSize="small" />
				<div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					{item.config.label}
				</div>
			</div>
		))
	);

	const SortableList: React.ComponentClass<SortableContainerProps & { items: Array<WidgetTemplate> }> = (
		SortableContainer(({ items }: { items: Array<WidgetTemplate> }) => (
			<div
				className="sortable-list"
				style={{
					backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#F3F3F3",
					border: theme.palette.mode === "dark" ? "1px solid rgba(0, 0, 0, 0.87)" : "1px solid #E5E5E5",
				}}>
				{items.map((item: WidgetTemplate, index: number) => <SortableItem index={index} item={item} />)}
			</div>
		))
	);

	const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => {
		setList(arrayMoveImmutable([ ...list ], oldIndex, newIndex)
			.map((widget, index) => ({ ...widget, order: index })));
	};

	// Seeded on open so cancelling leaves the dashboard untouched.
	useEffect(() => {
		if (open) {
			setList([ ...widgets ].sort((a, b) => a.order - b.order));
		}
	}, [ open ]);

	return (
		<CustomDialog
			open={open}
			title={intl.formatMessage({ id: "CHANGE_ORDER" })}
			onClose={() => handleOnClose()}
			maxWidth="sm"
			dialogStyling={{ content: { backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#EEE" } }}
			actions={(
				<Button onClick={() => save()} size="small">
					{intl.formatMessage({ id: "SAVE" })}
				</Button>
			)}>
			<div className="change-status-order-dialog">
				<SortableList
					items={list}
					onSortEnd={onSortEnd}
					helperClass={"sortable-status-helper"} />
			</div>
		</CustomDialog>
	);
};

export default WidgetOrderDialog;
